import { PropertiesPanel } from '@/components/workflow/PropertiesPanel';
import { useTranslation } from '@/i18n/LanguageContext';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import { useWorkflowOptional } from '@/contexts/WorkflowContext';
import type { WorkflowBlock, Connector, BlockGroup, BlockDimensions } from '@/types/workflow';
import { MobileSheet } from './ui/MobileSheet';

interface MobilePropertiesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedBlock: WorkflowBlock | null;
  selectedGroup: BlockGroup | null;
  workflowName: string;
  onUpdateWorkflowName: (name: string) => void;
  onUpdateBlock: (id: string, updates: Partial<WorkflowBlock>) => void;
  onDeleteBlock: (id: string) => void;
  onUpdateGroup: (id: string, updates: Partial<BlockGroup>) => void;
  onDeleteGroup: (id: string) => void;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  blockDimensions: Record<string, BlockDimensions>;
  zoom: number;
  onMinimapNavigate: (x: number, y: number) => void;
  onNavigateToGroup?: (groupId: string | null) => void;
}

export function MobilePropertiesSheet({
  open, onOpenChange, selectedBlock, selectedGroup, workflowName, onUpdateWorkflowName,
  onUpdateBlock, onDeleteBlock, onUpdateGroup, onDeleteGroup,
  blocks, connectors, groups, canvasWidth, canvasHeight, viewportWidth, viewportHeight,
  scrollLeft, scrollTop, blockDimensions, zoom, onMinimapNavigate, onNavigateToGroup,
}: MobilePropertiesSheetProps) {
  const { t } = useTranslation();
  const { keyboardOffset } = useVisualViewport();
  // Connector-selection lives in SelectionModeContext + WorkflowContext;
  // forward to PropertiesPanel so the same surface is reused on mobile.
  const { selectedConnectorId } = useSelectionMode();
  const wf = useWorkflowOptional();
  // On Android the soft-keyboard overlays the bottom-sheet. Shrinking
  // `maxHeight` by the keyboard offset keeps the focused input above
  // the keyboard instead of hidden behind it. Zero on desktop/iOS so
  // the default `max-h-[80dvh]` applies.
  const sheetStyle = keyboardOffset > 0
    ? { paddingBottom: keyboardOffset, maxHeight: `calc(80dvh - ${keyboardOffset}px)` }
    : undefined;

  return (
    <MobileSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('blockProperties')}
      contentStyle={sheetStyle}
    >
      <PropertiesPanel
        selectedBlock={selectedBlock}
        selectedGroup={selectedGroup}
        selectedConnectorId={selectedConnectorId}
        workflowName={workflowName}
        onUpdateWorkflowName={onUpdateWorkflowName}
        onUpdateBlock={onUpdateBlock}
        onDeleteBlock={onDeleteBlock}
        onUpdateGroup={onUpdateGroup}
        onDeleteGroup={onDeleteGroup}
        onUpdateConnector={wf?.updateConnector}
        onDeleteConnector={wf?.deleteConnector}
        onUpdateConnectorWaypoints={wf?.updateConnectorWaypoints}
        onCreateConnector={wf?.createConnector}
        blocks={blocks}
        connectors={connectors}
        groups={groups}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        viewportWidth={viewportWidth}
        viewportHeight={viewportHeight}
        scrollLeft={scrollLeft}
        scrollTop={scrollTop}
        blockDimensions={blockDimensions}
        zoom={zoom}
        onMinimapNavigate={onMinimapNavigate}
        onNavigateToGroup={onNavigateToGroup}
      />
    </MobileSheet>
  );
}
